import { useFormikContext } from 'formik';
import * as _ from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Keyboard,
  Platform,
  ReturnKeyTypeOptions,
  TextInput,
  TextInputProps,
  ActivityIndicator,
  TouchableHighlightProps,
  LayoutRectangle,
  TextProps,
  Modal,
  View
} from 'react-native';
import styled from 'styled-components/native';
import { TextInputMask, TextInputMaskProps } from 'react-native-masked-text';
import Colors from '../../constants/Colors';
import { useFormContext } from './Form';
import {
  internationalPhoneMask,
  InternationalPhoneNumber
} from '../../phone-number/InternationalPhoneNumberMask';
import { useHttpFetchApi } from '../../hooks/http/useHttpFetch';
import { Feather } from '@expo/vector-icons';
import { TouchableHighlight } from 'react-native-gesture-handler';
import SelectField, { SelectOption } from './SelectField';
import FlatListPicker from '../FlatlistPicker';
import { B } from '../Typography';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type FormInputVariant = 'outlined' | 'survey' | 'inline';

const FormInputView = styled.View`
  width: 100%;
`;

const LabelText = styled.Text<{ error?: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.error ? Colors.error : Colors.secondaryText)};
  margin-bottom: 2px;
`;

const StyledTextInputView = styled.View<{
  hasHelperText: boolean;
  multiLine?: boolean;
  error?: boolean;
}>`
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  min-height: 66px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
  flex-direction: row;
  align-items: center;
`;

const StyledTextInput = styled(TextInputMask)<{
  minHeight?: number;
  variant?: FormInputVariant;
  multiLine?: boolean;
  empty?: boolean;
}>`
  padding: ${p =>
    p.variant === 'survey' && (Platform.OS === 'ios' || Platform.OS === 'web')
      ? '20px 16px'
      : '20px 12px'};
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  ${p => (p.variant === 'survey' ? `font-size: 15px;` : `font-size: 18px;`)}
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  background-color: transparent;
  flex: 1;
  padding-right: 8px;
` as React.FC<
  TextInputMaskProps & {
    minHeight?: number;
    variant?: FormInputVariant;
    multiLine?: boolean;
    refInput: any;
    empty?: boolean;
  }
>;

const StyledModalButton = styled.TouchableOpacity`
  height: 20px;
  margin-right: 16px;
`;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)<{ errorTextColor: string }>`
  color: ${p =>
    p.errorTextColor === 'dark' ? Colors.error : Colors.errorCaption};
`;

const StyledValidateView = styled.View`
  padding: 0 12px;
`;

const StyledTouchableHighlight = styled.TouchableHighlight<{
  hasHelperText: boolean;
  variant?: FormInputVariant;
}>`
  position: relative;
  background-color: ${p =>
    p.variant === 'survey'
      ? Colors.primary
      : p.variant === 'inline'
      ? 'rgba(255, 255, 255, 0)'
      : '#fff'};
  border-radius: ${p => (p.variant === 'survey' ? 26.5 : 8)}px;
  padding: ${p => (p.variant === 'inline' ? '3' : '16')}px;
  /* color: #B3B3B3 */
  height: 64px;
  justify-content: center;
` as React.FC<
  TouchableHighlightProps & {
    hasHelperText: boolean;
    ref: any;
    variant?: FormInputVariant;
  }
>;

const StyledText = styled.Text<{
  value?: string;
  variant?: FormInputVariant;
  empty?: boolean;
}>`
  color: ${p =>
    p.value
      ? p.variant === 'survey'
        ? '#fff'
        : p.variant === 'inline'
        ? `${Colors.primaryPurple}`
        : '#000'
      : '#bbbb'};
  font-size: ${p => (p.variant === 'inline' ? '16' : '18')}px;
  font-weight: ${p => (p.variant === 'inline' ? 'bold' : 'normal')};
  padding-right: 32px;

  padding-left: ${p => (p.variant === 'survey' ? 5 : 0)}px;
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  /* white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; */
` as React.FC<
  TextProps & {
    value?: string;
    variant?: FormInputVariant;
    empty?: boolean;
  }
>;

const StyledDropDownIcon = styled.View<{ variant?: FormInputVariant }>`
  position: absolute;
  align-self: center;
  right: 0px;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledCountryPickerView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${Platform.OS === 'web' ? '14px' : '0'};
`;

const StyledPickerView = styled.View`
  background-color: white;
  width: 100%;
  padding: ${Platform.OS === 'web' ? '24px 10px 10px 10px' : '10px'};
  max-width: ${Platform.OS === 'web' ? '308px' : '280px'};
  border-radius: 8px;
`;

const StyledModalButtonView = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  margin-top: 32px;
  padding-bottom: 4px;
`;

export type FormInputProps = TextInputProps & {
  name: string;
  label?: string;
  helperText?: string;
  multiline?: boolean;
  returnKeyType?: ReturnKeyTypeOptions;
  submitOnReturn?: boolean;
  variant?: FormInputVariant;
  dismissKeyboard?: boolean;
  errorTextColor?: 'dark' | 'light';
  inputType?: 'phone' | 'default';
  mask?: string;
  onChangeText?: (text: string) => void;
  isValid?: (isValid: boolean, countryInfo?: string) => void;
};

const FormMaskedInput: React.FC<FormInputProps> = ({
  name,
  label,
  helperText,
  multiline,
  returnKeyType,
  submitOnReturn,
  variant = 'outlined',
  dismissKeyboard,
  errorTextColor = 'light',
  inputType = 'default',
  mask = 'SSSSSSSSSSSSSSS',
  onChangeText = (text: string) => ({}),
  isValid = (isValid: boolean, country?: string) => ({}),
  ...props
}) => {
  const insets = useSafeAreaInsets();
  const textInputRef = useRef<TextInput>(null!);
  const touchableHighlightRef = useRef<TouchableHighlight>(null!);
  const [layoutRect, setLayoutRect] = useState<LayoutRectangle>();
  const [openMenu, setOpenMenu] = useState(false);
  const {
    handleBlur,
    touched,
    errors,
    values,
    submitForm,
    setFieldValue,
    isSubmitting,
    isValidating
  } = useFormikContext<any>();
  const { setFocusedInput, addInput, focusNext } = useFormContext();
  const [height, setHeight] = useState<number>();
  const error = touched[name] && errors[name];
  const [phoneNumberMask, setPhoneNumberMask] =
    useState<InternationalPhoneNumber>({
      code: 'US',
      dial_code: '+1',
      mask: '+1 (999) 999 9999',
      name: 'United States'
    });
  const [twilioError, setTwilioError] = useState<string>();
  const { loading, error: phoneValidationError, postJson } = useHttpFetchApi();
  const [initPhone, setInitPhone] = useState<boolean>(false);
  const [countryNumber, setCountryNumber] = useState<string>('');
  const [selectedCountryInfo, setSelectedCountryInfo] = useState<string>('');

  const handleTwilioLookup = useCallback(
    async (phoneNumber: string) => {
      const result = await postJson<{
        valid: boolean;
        validationErrors: string[];
      }>('twilio/lookup', { phoneNumber: phoneNumber || '' });
      setTwilioError(result?.valid ? undefined : `Not a valid phone number`);
      if (result?.valid && isValid) {
        isValid(true, selectedCountryInfo);
      } else {
        isValid(false, selectedCountryInfo);
      }
    },
    [isValid, postJson, selectedCountryInfo]
  );

  // initial phone number check
  useEffect(() => {
    if (inputType === 'phone' && values[name] && selectedCountryInfo) {
      if (!initPhone) {
        const textWithDialCode = `${phoneNumberMask.dial_code}${values[name]}`;
        handleTwilioLookup(textWithDialCode);
      }
      setInitPhone(true);
    }
  }, [
    handleTwilioLookup,
    initPhone,
    inputType,
    name,
    values,
    selectedCountryInfo,
    phoneNumberMask
  ]);

  const handlePhoneNumberMask = useCallback(
    (text: string, countryPhonePrefix?: string) => {
      let matched = false;
      let lengthMatched = 0;
      internationalPhoneMask.forEach(data => {
        if (text.includes(data.dial_code)) {
          if (data.mask && data.dial_code.length >= lengthMatched) {
            lengthMatched = data.dial_code.length;
            setFieldValue(name, text.replace(`${data.dial_code}`, ''), true);
            setPhoneNumberMask(data);
            const internationalPhoneNumber = internationalPhoneMask.find(
              o => o.code === values['phoneNumberCode']
            );
            setSelectedCountryInfo(
              countryPhonePrefix
                ? countryPhonePrefix
                : internationalPhoneNumber
                ? `${internationalPhoneNumber.code} ${internationalPhoneNumber.name} ${internationalPhoneNumber.dial_code}`
                : `${data.code} ${data.name} ${data.dial_code}`
            );
            matched = true;
          }
        }
      });
    },
    []
  );

  const initialValueRef = useRef(values[name]);
  useEffect(() => {
    if (initialValueRef.current) {
      handlePhoneNumberMask(initialValueRef.current);
    } else {
      setSelectedCountryInfo('US United States +1');
    }
  }, [handlePhoneNumberMask]);

  const formattedCountryCode = useMemo(() => {
    const countryCodeValue = selectedCountryInfo.split(' ')[0];
    const dialCodeValue = selectedCountryInfo.split(' ').slice(-1)[0];
    const country = internationalPhoneMask.find(
      o => o.dial_code === dialCodeValue && o.code === countryCodeValue
    );
    return country ? `${country.code} ${country.dial_code}` : '';
  }, [selectedCountryInfo, internationalPhoneMask]);

  const formattedMaskedCountryNumber = useMemo(() => {
    const { mask, dial_code } = phoneNumberMask;
    const maskWithoutDialCode = mask.replace(`${dial_code} `, '');
    return maskWithoutDialCode === mask
      ? mask.replace(/.*\)\s*/, '')
      : maskWithoutDialCode;
  }, [phoneNumberMask]);

  return (
    <FormInputView>
      {label && <LabelText error={Boolean(error)}>{label}</LabelText>}
      <StyledTextInputView
        hasHelperText={Boolean(helperText)}
        error={Boolean(error || phoneValidationError || twilioError)}
      >
        <StyledTouchableHighlight
          variant={variant}
          ref={(touchableHighlight: TouchableHighlight) => {
            touchableHighlightRef.current = touchableHighlight;
          }}
          underlayColor={Colors.primary + '11'} // add alpha hex
          onPress={() => {
            (touchableHighlightRef.current as any).measureInWindow(
              (x: number, y: number, width: number, height: number) => {
                // y -= scrollDeltaY; // TODO: need to get scroll offset if in a scroll view
                setLayoutRect({
                  x,
                  y: y + (Platform.OS === 'android' ? insets.top : 0),
                  width,
                  height
                });
                setOpenMenu(true);
              }
            );
          }}
          hasHelperText={Boolean(helperText)}
        >
          <View>
            <StyledText
              value={selectedCountryInfo}
              numberOfLines={1}
              variant={variant}
              empty={true}
            >
              {formattedCountryCode}
            </StyledText>
            <StyledDropDownIcon variant={variant}>
              {variant === 'survey' ? (
                <Feather name='chevron-down' size={40} color='#fff' />
              ) : variant === 'inline' ? (
                <Feather
                  name='chevron-down'
                  size={18}
                  color={Colors.primaryPurple}
                />
              ) : (
                <Feather name='chevron-down' size={24} color={Colors.primary} />
              )}
            </StyledDropDownIcon>
          </View>
        </StyledTouchableHighlight>
        <StyledTextInput
          type='custom'
          keyboardType={inputType === 'phone' ? 'phone-pad' : 'default'}
          options={{
            mask: inputType === 'phone' ? formattedMaskedCountryNumber : mask
          }}
          multiline={multiline}
          blurOnSubmit={false}
          placeholderTextColor={error ? Colors.error : Colors.placeholderColor}
          refInput={(textInput: TextInput) => {
            textInputRef.current = textInput;
            addInput(textInput);
          }}
          {...props}
          minHeight={height}
          variant={variant}
          value={
            isSubmitting || isValidating
              ? values[name]?.replace(
                  `${selectedCountryInfo.split(' ').slice(-1)[0]}`,
                  ''
                )
              : values[name]
          }
          empty={!values[name]}
          returnKeyType={returnKeyType}
          onChangeText={text => {
            if (inputType === 'phone') {
              text = text.trim().replace(/[^0-9+]/g, '');
              if (isValid) {
                const textWithDialCode = `${phoneNumberMask.dial_code}${text}`;
                if (
                  !phoneNumberMask.min &&
                  textWithDialCode.length ===
                    phoneNumberMask.mask.trim().replace(/[^0-9+]/g, '').length
                ) {
                  handleTwilioLookup(textWithDialCode);
                } else if (
                  phoneNumberMask.min &&
                  textWithDialCode.length >= phoneNumberMask.min + 1
                ) {
                  handleTwilioLookup(textWithDialCode);
                } else {
                  isValid(false);
                }
              }
            }
            onChangeText(text);
            setFieldValue(name, text, true);
          }}
          onFocus={() => {
            setFocusedInput(textInputRef.current);
          }}
          onBlur={event => {
            handleBlur(name)(event);
            setFocusedInput(undefined);
            if (values[name] && values[name].length > 5) {
              handleTwilioLookup(values[name]);
            }
          }}
          onContentSizeChange={event => {
            setHeight(event.nativeEvent.contentSize.height);
          }}
          onSubmitEditing={() => {
            if (dismissKeyboard) {
              Keyboard.dismiss();
            } else if (submitOnReturn) {
              if (!multiline) {
                submitForm();
                Keyboard.dismiss();
              }
            } else {
              focusNext();
            }
          }}
        />
        {inputType === 'phone' && (
          <StyledValidateView>
            {loading ? (
              <ActivityIndicator size='small' />
            ) : values[name] ? (
              <Feather
                name={twilioError ? 'alert-triangle' : 'check'}
                size={24}
              />
            ) : (
              <></>
            )}
          </StyledValidateView>
        )}
        <Modal visible={openMenu} transparent={true} animationType='fade'>
          <StyledModalView>
            <StyledModalBackgroundContainer
              onPress={() => {
                setOpenMenu(false);
              }}
            >
              <StyledModalBackground />
            </StyledModalBackgroundContainer>
            <StyledPickerView>
              <StyledCountryPickerView>
                {Platform.OS === 'web' ? (
                  <SelectField
                    value={countryNumber || selectedCountryInfo}
                    options={internationalPhoneMask
                      .sort((a, b) => {
                        if (a.code === 'US') return -1;
                        if (b.code === 'US') return 1;
                        return a.code.localeCompare(b.code);
                      })
                      .map(
                        country =>
                          `${country.code} ${country.name} ${country.dial_code}`
                      )}
                    onChange={option => {
                      setCountryNumber(String((option as SelectOption).value));
                    }}
                  />
                ) : (
                  <FlatListPicker
                    ellipsizeMode='tail'
                    onValueChanged={data => {
                      setCountryNumber(data);
                    }}
                    initialValue={selectedCountryInfo}
                    options={internationalPhoneMask
                      .sort((a, b) => {
                        if (a.code === 'US') return -1;
                        if (b.code === 'US') return 1;
                        return a.code.localeCompare(b.code);
                      })
                      .map(
                        country =>
                          `${country.code} ${country.name} ${country.dial_code}`
                      )}
                  />
                )}
              </StyledCountryPickerView>
              <StyledModalButtonView>
                <StyledModalButton
                  onPress={() => {
                    setOpenMenu(false);
                  }}
                >
                  <B color='dark'>Cancel</B>
                </StyledModalButton>
                <StyledModalButton
                  onPress={() => {
                    const dialCode = countryNumber.split(' ').slice(-1)[0];
                    setOpenMenu(false);
                    isValid(false);
                    handlePhoneNumberMask(dialCode, countryNumber);
                  }}
                >
                  <B>Confirm</B>
                </StyledModalButton>
              </StyledModalButtonView>
            </StyledPickerView>
          </StyledModalView>
        </Modal>
      </StyledTextInputView>

      {twilioError || error ? (
        <ErrorText errorTextColor={errorTextColor}>
          {String(
            twilioError || error || phoneValidationError?.message || 'error'
          )}
        </ErrorText>
      ) : (
        helperText && <HelperText>{helperText}</HelperText>
      )}
    </FormInputView>
  );
};

export default FormMaskedInput;
